/**
 * adminTrpcContext — the admin-surface tRPC client seam for the 021-cms
 * `/admin/cms` surface (T036/T037 backbone). Part of the `useAdminContent`
 * decomposition: this module owns the production client factory, the React
 * Context, and the `useAdminTrpc` resolver; the query/mutation hooks resolve
 * the client through `useAdminTrpc` so tests substitute a mocked client at the
 * component boundary without coupling to network internals.
 *
 * The admin surface is internal-tooling, NOT a port concept (per the slice
 * brief): the member-side `ContentReaderPort` exists because the read-path has
 * a real "swap the adapter" use case (CMS vs. SeededContentSource); the
 * write-path has no such alternative adapter. So the hooks call the typed tRPC
 * client directly under `client.admin.content.*` rather than introducing a
 * parallel `ContentAuthoringPort` on the web side. The adapter (the tRPC
 * client) is resolved from React Context via `useAdminTrpc`, mirroring the
 * member-side pattern in `apps/web/src/content/hooks/useArticle.ts`.
 *
 * Cookie & client wiring (post-cutover, D-106 / T045)
 * ===================================================
 *
 * The composition root (`main.tsx`) constructs a bearer-less
 * `WebTrpcClient` and passes it to `AdminTrpcProvider`. The admin
 * surface now authenticates via the BFF session cookie
 * (`__Secure-oma_session`), which is `HttpOnly` and rides every
 * same-origin request automatically, so there is nothing for this hook
 * to read or attach. This is the admin half of the spec-023 cutover:
 * the admin audience resolves through `RealSessionProvider` (the BFF),
 * and the admin CMS tRPC calls authenticate through the same cookie.
 *
 * This REPLACES the previous dev-only `getBearerToken` derivation (an
 * email-shaped demo bearer pulled off the live `SessionPort` state). That
 * shape fail-closed in production (DEV=false sent no bearer, so the
 * `sessionProcedure`-gated admin routes returned UNAUTHORIZED); dropping
 * it FIXES production too, because the cookie authenticates admin in every
 * build, not just `vite dev`. No client token is held in JS at all (the
 * cookie is `HttpOnly`), which is the load-bearing BFF security property
 * (D-092 / RD-002).
 */

import { createContext, type JSX, type ReactNode, useContext, useState } from 'react';

import { createWebTrpcClient, type WebTrpcClient } from '../../../trpc/client.js';

// ---------------------------------------------------------------------
// Production client factory
// ---------------------------------------------------------------------

/**
 * Build the admin-surface `WebTrpcClient` for production composition.
 *
 * Post-cutover (spec 023, D-106 / T045) the admin surface authenticates via
 * the BFF session cookie (`__Secure-oma_session`), NOT a bearer token. The
 * cookie is `HttpOnly` and rides every same-origin request automatically, so
 * the client is constructed bearer-less: there is no `getBearerToken`, no
 * `Authorization` header, nothing for this hook to read off any session
 * state. The `sessionProcedure`-gated admin routes accept the cookie-derived
 * server-side session, so the admin CMS works in every build (this drops the
 * previous dev-only email-bearer derivation, which fail-closed in production).
 *
 * No `SessionPort` dependency: the previous shape read `sessionPort.state.email`
 * to synthesise a demo bearer; the cookie carries identity now, so the hook
 * takes no arguments. Holding no token in JS is the load-bearing BFF security
 * property (D-092 / RD-002): a token in JS would be an XSS exfiltration target.
 *
 * The client is constructed once per React root via `useState`'s lazy
 * initialiser (the standard "stable factory result" pattern used by
 * `useContentPort`, `useSessionPort`, `useLanguagePort`). It carries no
 * per-render state.
 *
 * The composition root (`main.tsx`) passes the result into
 * `<AdminTrpcProvider client={...}>`. Tests mount `AdminTrpcProvider`
 * directly with a hand-rolled mock client and skip this factory.
 */
export function useAdminTrpcClient(): WebTrpcClient {
  const [client] = useState<WebTrpcClient>(() => createWebTrpcClient());
  return client;
}

// ---------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------

const AdminTrpcContext = createContext<WebTrpcClient | null>(null);

/**
 * Mount the admin-surface tRPC client in React Context. The
 * composition root (`main.tsx`) wraps the `/admin/cms` subtree (or the
 * whole app — cheap because the client itself is lazy) in this
 * provider; tests wrap the UI under test directly so they can hand in
 * a mocked client.
 */
export function AdminTrpcProvider({
  client,
  children,
}: {
  readonly client: WebTrpcClient;
  readonly children: ReactNode;
}): JSX.Element {
  return <AdminTrpcContext.Provider value={client}>{children}</AdminTrpcContext.Provider>;
}

/**
 * Resolve the admin tRPC client from context. Throws a clear error if
 * the consumer is mounted outside `AdminTrpcProvider` (mirrors the
 * `useSession` / `useContent` missing-port error shape).
 */
export function useAdminTrpc(): WebTrpcClient {
  const client = useContext(AdminTrpcContext);
  if (client === null) {
    throw new Error(
      'useAdminTrpc must be used within an AdminTrpcProvider that provides the admin tRPC client',
    );
  }
  return client;
}

/**
 * Non-throwing variant: resolve the admin tRPC client, or `null` when no
 * `AdminTrpcProvider` is in scope.
 *
 * `useMediaUpload()` needs this because it runs UNCONDITIONALLY as a hook
 * inside `ImageUploadField`, and that field is mounted in component tests
 * (with an injected upload seam) WITHOUT an `AdminTrpcProvider`. The throwing
 * `useAdminTrpc()` would break those mounts; this resolver lets the hook read
 * context safely and defer any "no provider" failure to invocation time (the
 * default upload callback rejects only when actually called — which never
 * happens in production, where admin forms always sit inside the provider).
 * The raw context stays private to this module.
 */
export function useOptionalAdminTrpc(): WebTrpcClient | null {
  return useContext(AdminTrpcContext);
}
